import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useServicePermission = (serviceId: string | number | undefined) => {
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkPermissions = async () => {
      if (!serviceId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        // CONSISTENT: Keep using the has_role RPC function for admin check
        const { data: hasAdminRole, error: roleError } = await supabase.rpc('has_role', {
          _role: 'admin'
        });
        
        if (roleError) {
          console.error("Error checking admin role:", roleError);
          setIsAdmin(false);
        } else {
          setIsAdmin(!!hasAdminRole);
        }
        
        // Convert serviceId to number if it's a string
        const numericServiceId = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;
        
        // Get service details
        const { data: service, error } = await supabase
          .from('services')
          .select('owner_id, vendor_can_edit')
          .eq('id', numericServiceId)
          .maybeSingle();

        if (error) {
          console.error("Error fetching service:", error);
          toast({
            title: "Error",
            description: "Failed to check service permissions",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        // Check if user is owner
        const userIsOwner = service?.owner_id === user.id;
        setIsOwner(userIsOwner);
        
        // Determine if user can edit
        // Admin can always edit, owner can edit if vendor_can_edit is true
        setCanEdit(!!hasAdminRole || (userIsOwner && service?.vendor_can_edit));
      } catch (error) {
        console.error("Permission check error:", error);
        toast({
          title: "Error",
          description: "Failed to check service permissions",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    checkPermissions();
  }, [serviceId, toast]);

  return { isOwner, isAdmin, canEdit, loading };
};
